<template>
    <div class="my-4">
        <label class="text-primary-200 mb-4 block text-sm uppercase tracking-wide" v-if="!hideLabel">
            <t :path="label" />
        </label>

        <SegmentedControl
            :model-value="input"
            :options="segmentedOptions"
            :disabled="inputDisabled"
            :stack-on-mobile="stackOnMobile"
            @update:model-value="update"
        />

        <p class="small text-primary-300 mt-4" v-if="footer">
            <t :path="footer" />
        </p>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
import SegmentedControl from '~/components/ui/SegmentedControl.vue';

export default defineComponent({
    components: { SegmentedControl },

    props: {
        name: {
            type: String,
            required: true,
        },
        options: {
            type: [Array, Object],
            required: true,
        },
        value: {
            type: [String, Number],
        },
        footer: {
            type: String,
        },
        translationPrefix: {
            type: String,
        },
        hideLabel: {
            type: Boolean,
        },
        stackOnMobile: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update:value'],

    setup(props) {
        const input = ref<string | null>(typeof props.value !== 'undefined' ? props.value.toString() : null);

        watch(
            () => props.value,
            (newValue) => {
                input.value = newValue !== undefined ? newValue.toString() : null;
            },
        );

        const inputDisabled = ref<boolean>(false);

        let unregister: any;

        onBeforeMount(() => {
            unregister = inject<registerFormComponentFn | null>(
                'registerFormComponent',
                null,
            )?.({
                key: props.name,
                value: input,
                onSubmit: (state) => {
                    inputDisabled.value = state;
                },
            });
        });

        onBeforeUnmount(() => {
            unregister?.();
            unregister = null;
        });

        return {
            inputDisabled,
            input,
            label: computed(() => `components.form.fields.${props.name}`),
            segmentedOptions: computed(() =>
                Object.entries(props.options as Record<string, string>).map(([key, value]) => ({
                    value: key.toString(),
                    labelPath: `${props.translationPrefix ? `${props.translationPrefix}.` : ''}${value}`,
                })),
            ),
        };
    },

    methods: {
        update(value: string) {
            this.input = value;

            this.$emit('update:value', this.input);
        },
    },
});
</script>
